import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Package, MapPin, Clock, CheckCircle2, AlertCircle, ArrowRight } from 'lucide-react';
import PublicNavbar from '../components/PublicNavbar';
import orderService from '../services/order.service';

const TrackOrder = () => {
  const [trackingId, setTrackingId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [trackingData, setTrackingData] = useState(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const id = searchParams.get('id') || searchParams.get('awb');
    if (id) {
      setTrackingId(id);
      performTrack(id);
    }
  }, [searchParams]);

  const performTrack = async (id) => {
    try {
      setLoading(true);
      setError('');
      setTrackingData(null);
      
      const response = await orderService.publicTrackOrder(id);
      if (response.success) {
        setTrackingData(response.data);
      } else {
        setError(response.message || 'Tracking failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to find tracking details. Please check your Order Number or AWB.');
    } finally {
      setLoading(false);
    }
  };

  const handleTrack = async (e) => {
    e?.preventDefault();
    if (!trackingId.trim()) return;
    performTrack(trackingId.trim());
  };

  const getStatusColor = (status) => {
    const s = status?.toLowerCase();
    if (s?.includes('delivered')) return 'text-emerald-600 bg-emerald-50';
    if (s?.includes('cancel')) return 'text-red-600 bg-red-50';
    if (s?.includes('transit') || s?.includes('ship')) return 'text-blue-600 bg-blue-50';
    return 'text-amber-600 bg-amber-50';
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <PublicNavbar />
      
      <main className="pt-32 pb-20 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Hero/Search Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-black text-slate-900 mb-4">Track Your Shipment</h1>
            <p className="text-slate-600 text-lg max-w-xl mx-auto">
              Enter your Order Number or AWB Number to get real-time updates on your delivery.
            </p>
          </div>

          {/* Search Box */}
          <div className="bg-white rounded-3xl shadow-xl shadow-slate-200 border border-slate-100 p-2 mb-12">
            <form onSubmit={handleTrack} className="flex flex-col md:flex-row gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                <input
                  type="text"
                  value={trackingId}
                  onChange={(e) => setTrackingId(e.target.value)}
                  placeholder="Enter Order Number or AWB (e.g. ORD123...)"
                  className="w-full pl-12 pr-4 py-4 rounded-2xl bg-slate-50 border-none focus:ring-2 focus:ring-indigo-500 outline-none text-slate-900 font-medium"
                />
              </div>
              <button
                type="submit"
                disabled={loading || !trackingId.trim()}
                className="bg-indigo-600 text-white px-8 py-4 rounded-2xl font-bold hover:bg-indigo-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-100"
              >
                {loading ? 'Searching...' : (
                  <>
                    Track Now
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-100 text-red-600 p-6 rounded-2xl flex items-start gap-4 mb-12 animate-in fade-in slide-in-from-top-4">
              <AlertCircle className="w-6 h-6 shrink-0" />
              <div>
                <h3 className="font-bold mb-1">Tracking Failed</h3>
                <p className="text-sm opacity-90">{error}</p>
              </div>
            </div>
          )}

          {/* Tracking Results */}
          {trackingData && (
            <div className="space-y-8 animate-in fade-in zoom-in-95 duration-500">
              {/* Summary Card */}
              <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-6 mb-8 border-b border-slate-100 pb-8">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-indigo-100 rounded-2xl flex items-center justify-center text-indigo-600">
                      <Package className="w-7 h-7" />
                    </div>
                    <div>
                      <div className="text-sm font-bold text-slate-400 uppercase tracking-wider">Order Number</div>
                      <div className="text-xl font-black text-slate-900">{trackingData.orderInfo.orderNumber}</div>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-4">
                    {trackingData.orderInfo.awb && (
                      <div className="px-4 py-2 bg-slate-100 rounded-xl text-sm font-bold text-slate-600 border border-slate-200">
                        AWB: {trackingData.orderInfo.awb}
                      </div>
                    )}
                    <div className={`px-4 py-2 rounded-xl text-sm font-bold border border-current ${getStatusColor(trackingData.orderInfo.status)}`}>
                      {trackingData.orderInfo.status}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
                  <div>
                    <div className="text-xs font-bold text-slate-400 uppercase mb-1">Courier Partner</div>
                    <div className="font-bold text-slate-800">{trackingData.orderInfo.courierName || 'Pending Assignment'}</div>
                  </div>
                  <div>
                    <div className="text-xs font-bold text-slate-400 uppercase mb-1">Estimated Delivery</div>
                    <div className="font-bold text-slate-800">
                      {trackingData.orderInfo.status === 'cancelled'
                        ? 'Cancelled'
                        : trackingData.tracking.estimated_delivery || 'Calculating...'}
                    </div>
                  </div>
                  <div className="col-span-2 md:col-span-1">
                    <div className="text-xs font-bold text-slate-400 uppercase mb-1">Current Location</div>
                    <div className="font-bold text-slate-800 flex items-center gap-1">
                      <MapPin className="w-4 h-4 text-slate-400" />
                      {trackingData.orderInfo.status === 'cancelled'
                        ? 'Cancelled'
                        : trackingData.tracking.current_location || 'Updating...'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Activity Timeline */}
              <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm">
                <h2 className="text-xl font-black text-slate-900 mb-8 flex items-center gap-3">
                  <Clock className="w-6 h-6 text-indigo-600" />
                  Shipment Activity
                </h2>

                <div className="relative">
                  {/* Timeline Line */}
                  <div className="absolute left-[11px] top-2 bottom-2 w-0.5 bg-slate-100"></div>

                  <div className="space-y-10">
                    {(trackingData.tracking.activities || []).map((activity, idx) => (
                      <div key={idx} className="relative pl-10">
                        {/* Dot */}
                        <div className={`absolute left-0 top-1 w-6 h-6 rounded-full border-4 border-white shadow-sm flex items-center justify-center ${idx === 0 ? 'bg-indigo-600 ring-4 ring-indigo-50' : 'bg-slate-300'}`}>
                          {idx === 0 && <CheckCircle2 className="w-3 h-3 text-white" />}
                        </div>
                        
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                          <div>
                            <h3 className={`font-bold ${idx === 0 ? 'text-slate-900 text-lg' : 'text-slate-600'}`}>
                              {activity.activity || activity.status}
                            </h3>
                            <p className="text-sm text-slate-500 font-medium flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {activity.location || 'Location shared during scan'}
                            </p>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-bold text-slate-900">
                              {activity.timestamp ? new Date(activity.timestamp).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
                            </div>
                            <div className="text-xs font-medium text-slate-400">
                              {activity.timestamp ? new Date(activity.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {(!trackingData.tracking.activities || trackingData.tracking.activities.length === 0) && (
                      <div className="text-center py-10">
                        <Package className="w-12 h-12 text-slate-200 mx-auto mb-4" />
                        <p className="text-slate-400 font-medium">Tracking history will appear once the courier scans your shipment.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default TrackOrder;
